import { type as osType } from '@tauri-apps/plugin-os';

/**
 * Checks if the app is running in a Tauri environment
 * @returns boolean indicating if we're in a Tauri app
 */
export function isTauri(): boolean {
  return typeof window !== 'undefined' && (window as any).__TAURI__ !== undefined;
}

/**
 * Checks if the app is running in a web browser environment
 * @returns boolean indicating if we're in a web browser
 */
export function isWeb(): boolean {
  return !isTauri();
}

/**
 * Checks if the app is running on macOS
 * @returns boolean indicating if we're on macOS
 */
export async function isMacOS(): Promise<boolean> {
  // Android overlay is a plain WebView - return early before calling Tauri plugins
  if (typeof window !== 'undefined' && (window as any).AndroidBridge) return false;
  if (isWeb()) return false;
  try {
    const { platform } = await import('@tauri-apps/plugin-os');
    return platform() === 'macos';
  } catch (e) {
    console.error('Failed to detect OS:', e);
    return false;
  }
}

/**
 * Detects if the app is running on a mobile device
 * @returns boolean indicating if we're on a mobile device
 */
export function isMobile(): boolean {
  // Android overlay is a plain WebView - return early before calling Tauri plugins
  if (typeof window !== 'undefined' && (window as any).AndroidBridge) return true;
  if (isWeb()) {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }
  try {
    const type = osType();
    return type === 'android' || type === 'ios';
  } catch (e) {
    console.error('Failed to detect OS type:', e);
    return false;
  }
}

/**
 * Checks if the app is running on Android (Tauri only - Android web builds
 * aren't a thing, so this deliberately doesn't handle the isWeb() case).
 * @returns boolean indicating if we're on Android
 */
export function isAndroid(): boolean {
  // Android overlay is a plain WebView - return early before calling Tauri plugins
  if (typeof window !== 'undefined' && (window as any).AndroidBridge) return true;
  if (isWeb()) return false;
  try {
    return osType() === 'android';
  } catch (e) {
    console.error('Failed to detect OS type:', e);
    return false;
  }
}

/**
 * Gets the current platform (web, desktop, or mobile)
 * @returns string indicating the platform
 */
export function getPlatform(): 'web' | 'desktop' | 'mobile' {
  if (isWeb()) {
    return isMobile() ? 'mobile' : 'web';
  }
  return isMobile() ? 'mobile' : 'desktop';
}

/**
 * Precise client platform label, sent to the backend (X-Client-Platform header,
 * and the Google OAuth `state` param - see apiService.ts/server/routes/auth.js)
 * so admin can tell which platform a login came from. Deliberately distinct from
 * getPlatform() above: a packaged desktop app and a plain browser on the same OS
 * send near-identical User-Agent strings, so the server can't reliably tell them
 * apart on its own - this is the client self-reporting instead.
 */
export function getClientPlatform(): 'android' | 'ios' | 'windows' | 'macos' | 'linux' | 'web' {
  // Android overlay is a plain WebView - return early before calling Tauri plugins
  if (typeof window !== 'undefined' && (window as any).AndroidBridge) return 'android';
  if (isWeb()) return 'web';
  try {
    const type = osType();
    if (type === 'android' || type === 'ios' || type === 'windows' || type === 'macos' || type === 'linux') {
      return type;
    }
    return 'web';
  } catch (e) {
    console.error('Failed to detect client platform:', e);
    return 'web';
  }
}